import { createStore, applyMiddleware, compose } from 'redux';
import createSagaMiddleware from 'redux-saga';
import _get from 'lodash/get';
import _difference from 'lodash/difference';

import { reportError } from 'helpers/error-logging';
import { shouldRedirectToLoginForResponse } from 'helpers/session-auth-http';
import { redirectToLoginOnce } from 'helpers/session-auth-redirect';
import { watchMediaState } from 'reducers/ui';
import rootReducer from 'reducers/rootReducer';
import { watchApp, watchLocalStorageMessages } from 'features/App';

export const sagaMiddleware = createSagaMiddleware({
  onError: error => {
    if (process.env.NODE_ENV === 'test') return;

    reportError(error);
    const status = _get(error, 'response.status');
    const config = error.config || {};
    if (shouldRedirectToLoginForResponse(config, status)) {
      redirectToLoginOnce(status);
    }
  },
});

// this will append and spread arguments to the applyMiddleware() invocation parameters
function defaultMiddlewares(...args) {
  const middlewares = [applyMiddleware(sagaMiddleware, ...args)];
  // eslint-disable-next-line no-underscore-dangle
  if (window?.__REDUX_DEVTOOLS_EXTENSION__) {
    // eslint-disable-next-line no-underscore-dangle
    middlewares.push(window.__REDUX_DEVTOOLS_EXTENSION__());
  }
  return compose(...middlewares);
}

function createStoreWithParams(params = {}) {
  const { sagaDependencies, userInfo } = params;
  const initialEntities = {};

  if (userInfo) {
    initialEntities.userInfo = {
      data: { default: userInfo },
      error: { default: null },
      loading: { default: false, lastKey: 'default' },
      loaded: { default: true, lastKey: 'default' },
    };
  }

  let initialState = !initialEntities ? {} : { entities: initialEntities };
  if (window?.store) {
    initialState = window?.store?.getState();
  }
  const store = createStore(rootReducer, initialState, defaultMiddlewares());

  store.sagaDependencies = { ...sagaDependencies };

  store.queue = Promise.resolve();
  store.runningSagas = new Map();
  store.stopSagasByKey = {};

  store.runSagas = (key, sagas, ...args) => {
    const expiredSagas = _difference(store.stopSagasByKey[key] || [], sagas);
    expiredSagas.forEach(store.stopSaga);
    store.stopSagasByKey[key] = [];
    sagas.forEach(saga => store.runSaga(saga, ...args));
  };

  store.stopSagas = (key, sagas) => {
    if (sagas.length) {
      store.stopSagasByKey[key] = sagas;
    }
  };

  store.runSaga = (saga, ...args) => {
    if (!store.runningSagas.has(saga)) {
      const task = sagaMiddleware.run(saga, ...args);
      store.runningSagas.set(saga, task);
      return task.toPromise().finally(() => store.stopSaga(saga));
    }

    return store.runningSagas.get(saga).toPromise();
  };

  store.stopSaga = saga => {
    const task = store.runningSagas.get(saga);

    if (task && task.isRunning()) {
      task.cancel();
    }

    store.runningSagas.delete(saga);
  };

  if (process.env.NODE_ENV !== 'test') {
    // Run watchApp at bootstrap so SESSION_AUTH_EXPIRED is always caught — including when
    // the dispatch comes from UnwrappedRoute's blockedByVenueAccess effect, which fires
    // before per-route saga registration would otherwise install the listener.
    store.runSaga(watchApp, store.sagaDependencies);
    watchMediaState(store.dispatch);
    watchLocalStorageMessages(store.dispatch);
  }

  return store;
}

export default createStoreWithParams;
